import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingPage from '../components/Loader';
import RestaurantNavbar from '../components/RestaurantNavbar';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  FaBell, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaClock,
  FaEye,
  FaShoppingBag,
  FaReceipt,
  FaHistory,
  FaChevronRight,
  FaUtensils,
  FaStore,
  FaUsers,
  FaChartLine
} from "react-icons/fa";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [visibleNotifications, setVisibleNotifications] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    const token = localStorage.getItem('token');

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        'https://mongobyte-hc73.onrender.com/api/v1/restaurants/notifications',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Handle different response structures
      const notificationData = response.data?.notifications || response.data || [];
      setNotifications(Array.isArray(notificationData) ? notificationData.reverse() : []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setError("Failed to load notifications. Please try again.");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const getNotificationIcon = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('order') || lowerMessage.includes('new order')) 
      return <FaShoppingBag className="text-pepperoni" />;
    if (lowerMessage.includes('payment') || lowerMessage.includes('paid')) 
      return <FaReceipt className="text-cheese" />;
    if (lowerMessage.includes('customer') || lowerMessage.includes('review')) 
      return <FaUsers className="text-blue-500" />;
    if (lowerMessage.includes('menu') || lowerMessage.includes('meal')) 
      return <FaUtensils className="text-green-500" />;
    if (lowerMessage.includes('restaurant') || lowerMessage.includes('profile')) 
      return <FaStore className="text-purple-500" />;
    if (lowerMessage.includes('analytics') || lowerMessage.includes('report')) 
      return <FaChartLine className="text-indigo-500" />;
    if (lowerMessage.includes('warning') || lowerMessage.includes('alert')) 
      return <FaExclamationTriangle className="text-red-500" />;
    return <FaBell className="text-crust" />;
  };

  const getNotificationBorder = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('order') || lowerMessage.includes('new order')) 
      return 'border-l-pepperoni bg-red-50/30';
    if (lowerMessage.includes('payment') || lowerMessage.includes('paid')) 
      return 'border-l-cheese bg-yellow-50/30';
    if (lowerMessage.includes('customer') || lowerMessage.includes('review')) 
      return 'border-l-blue-500 bg-blue-50/30';
    if (lowerMessage.includes('menu') || lowerMessage.includes('meal')) 
      return 'border-l-green-500 bg-green-50/30';
    if (lowerMessage.includes('restaurant') || lowerMessage.includes('profile')) 
      return 'border-l-purple-500 bg-purple-50/30';
    if (lowerMessage.includes('analytics') || lowerMessage.includes('report')) 
      return 'border-l-indigo-500 bg-indigo-50/30';
    if (lowerMessage.includes('warning') || lowerMessage.includes('alert')) 
      return 'border-l-red-500 bg-red-50/30';
    return 'border-l-gray-300 bg-gray-50/30';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown time';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShowMore = () => {
    setVisibleNotifications((prev) => prev + 10);
  };

  const getNotificationActions = (notification) => {
    const message = notification.message.toLowerCase();
    const actions = [];

    // Order-related notifications
    if (message.includes('order')) {
      actions.push({
        type: 'view_orders',
        label: 'View Orders',
        onClick: () => navigate('/restaurant/dashboard')
      });
    }

    // Payment-related notifications
    if (message.includes('payment') || message.includes('paid') || message.includes('transaction')) {
      actions.push({
        type: 'view_transactions',
        label: 'View Transactions',
        onClick: () => navigate('/restaurant/transactions')
      });
    }

    // Menu-related notifications
    if (message.includes('menu') || message.includes('meal')) {
      actions.push({
        type: 'view_menu',
        label: 'Manage Menu',
        onClick: () => navigate('/restaurant/menu')
      });
    }

    // Analytics notifications
    if (message.includes('analytics') || message.includes('report') || message.includes('review')) {
      actions.push({
        type: 'view_dashboard',
        label: 'View Dashboard',
        onClick: () => navigate('/restaurant/dashboard')
      });
    }

    return actions;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <RestaurantNavbar />
      
      <div className="py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-crust mb-2 flex items-center justify-center gap-3 font-secondary">
              <FaBell className="text-pepperoni" />
              Restaurant Notifications
            </h1>
            <p className="text-gray-600 font-sans">Stay updated with your restaurant activities</p>
          </motion.div>

          {/* Error State */}
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border-l-4 border-pepperoni rounded-lg p-4 mb-6 flex items-center gap-3"
            >
              <FaExclamationTriangle className="text-pepperoni flex-shrink-0" />
              <span className="font-sans text-crust">{error}</span>
            </motion.div>
          )}

          {/* Notifications List */}
          <div className="space-y-3">
            {notifications.length > 0 ? (
              <AnimatePresence>
                {notifications.slice(0, visibleNotifications).map((notification, index) => {
                  const actions = getNotificationActions(notification);
                  const borderClass = getNotificationBorder(notification.message);
                  return (
                    <motion.div
                      key={notification._id || notification.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`bg-white rounded-lg shadow-sm border-l-4 ${borderClass} p-4 hover:shadow-md transition-all duration-200 cursor-pointer group`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5 text-lg">
                          {getNotificationIcon(notification.message)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-crust font-medium leading-relaxed font-sans text-sm mb-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                            <FaClock className="text-xs" />
                            <span className="font-sans">
                              {formatDate(notification.createdAt || notification.timestamp)}
                            </span>
                          </div>
                          
                          {/* Action Buttons */}
                          {actions.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {actions.map((action, actionIndex) => (
                                <motion.button
                                  key={actionIndex}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick();
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-crust text-white text-xs font-medium rounded-md hover:bg-gray-800 transition-all duration-200 shadow-sm"
                                >
                                  {action.type === 'view_orders' && <FaShoppingBag />}
                                  {action.type === 'view_transactions' && <FaReceipt />}
                                  {action.type === 'view_menu' && <FaUtensils />}
                                  {action.type === 'view_dashboard' && <FaChartLine />}
                                  <span>{action.label}</span>
                                </motion.button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Subtle chevron indicator */}
                        <div className="flex-shrink-0 opacity-30 group-hover:opacity-60 transition-opacity duration-200">
                          <FaChevronRight className="text-crust text-xs" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            ) : (
              !loading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="mb-6">
                    <FaBell className="text-6xl text-gray-200 mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold text-crust mb-2 font-secondary">No notifications yet</h2>
                    <p className="text-gray-500 font-sans">You'll see your restaurant notifications here when they arrive!</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/restaurant/dashboard')}
                    className="bg-pepperoni text-white px-6 py-3 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-2 font-sans mx-auto"
                  >
                    <FaStore />
                    Go to Dashboard
                  </motion.button>
                </motion.div>
              )
            )}

            {/* Loading State */}
            {loading && <LoadingPage />}

            {/* Load More Button */}
            {visibleNotifications < notifications.length && !loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center pt-6"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleShowMore}
                  className="bg-crust text-white px-6 py-3 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-2 font-sans"
                >
                  <FaBell />
                  Show More ({notifications.length - visibleNotifications} remaining)
                </motion.button>
              </motion.div>
            )}
          </div>

          {/* Refresh Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center pt-8"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={fetchNotifications}
              disabled={loading}
              className="bg-cheese text-crust px-6 py-3 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-2 font-sans disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaHistory className={loading ? 'animate-spin' : ''} />
              {loading ? 'Refreshing...' : 'Refresh Notifications'}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
